import React, { useState } from 'react';
import { Button, DatePicker, Form, Input, Segmented, Select, Space, Tooltip, Typography, message } from 'antd';
import { CarOutlined, CheckOutlined, CopyOutlined, CreditCardOutlined, PhoneOutlined, UserOutlined } from '@ant-design/icons';
import { DataService } from '../../config/data/dataService';
import { LogisticsCard, SectionHeader, SideCard } from './HistoryDetail.styles';

const { Text } = Typography;

const PickupPaymentDetailsCard = ({ app }) => {
  const [pickupForm] = Form.useForm();
  const [pickupDetails, setPickupDetails] = useState(null);
  const [pickupMode, setPickupMode] = useState('self');
  const [savingPickup, setSavingPickup] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);

  const isAccepted = String(app?.status || '').toLowerCase().includes('accepted');
  const pickupUpdated = String(app?.rawStatus || '').toLowerCase() === 'pickup_updated';

  if (!isAccepted || pickupUpdated) return null;

  const handleCopy = (text, key, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    message.success(`${label} copied to clipboard!`);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handlePickupSubmit = async (values) => {
    setSavingPickup(true);
    try {
      const response = await DataService.post('/updatePickupDetails', {
        reference_no: app.referenceNo || app.id,
        pickup_type: pickupMode,
        pickup_person_name: values.pickupPersonName,
        mobile_number: values.pickupPersonNumber,
        ...(pickupMode === 'another' && {
          pickup_partner_name: values.pickupPartnerName.trim(),
          awb_no: values.awbNumber.trim(),
          tracking_url: values.trackingUrl.trim(),
        }),
        pickup_date: values.pickupDate.format('YYYY-MM-DD'),
        pickup_slot: values.pickupSlot,
        utr_number: values.utrNumber,
      });
      if (response.data.status === true) {
        setPickupDetails({
          ...values,
          pickupType: pickupMode,
        });
        message.success('Pickup and payment details updated');
      } else {
        message.error(response.data.message || 'Unable to update pickup details');
      }
    } catch (error) {
      console.error('Pickup details update error:', error);
      message.error('Unable to update pickup details');
    } finally {
      setSavingPickup(false);
    }
  };

  return (
    <SideCard>
      <SectionHeader $color="#0d9488">
        <div className="section-title">
          <div className="section-icon">
            <CarOutlined />
          </div>
          <span>Pickup & Payment Details</span>
        </div>
      </SectionHeader>

      {pickupDetails ? (
        <LogisticsCard>
          <div className="agent-header">
            <div className="agent-avatar">
              <UserOutlined />
            </div>
            <div>
              <div className="agent-name">{pickupDetails.pickupPersonName}</div>
              {pickupDetails.pickupPersonNumber ? (
                <a href={`tel:${pickupDetails.pickupPersonNumber}`} className="agent-phone">
                  <PhoneOutlined /> {pickupDetails.pickupPersonNumber}
                </a>
              ) : null}
            </div>
          </div>

          <div className="meta-grid">
            <div className="meta-box">
              <div className="box-label">Pickup Person Name</div>
              <div className="box-val">{pickupDetails.pickupPersonName || '-'}</div>
            </div>

            <div className="meta-box">
              <div className="box-label">Mobile Number</div>
              <div className="box-val">
                {pickupDetails.pickupPersonNumber ? (
                  <a href={`tel:${pickupDetails.pickupPersonNumber}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                    <PhoneOutlined /> {pickupDetails.pickupPersonNumber}
                  </a>
                ) : (
                  '-'
                )}
              </div>
            </div>

            <div className="meta-box">
              <div className="box-label">Pickup Mode</div>
              <div className="box-val">
                {pickupDetails.pickupType === 'self' ? 'Self Pickup' : pickupDetails.pickupPartnerName || 'Courier Partner'}
              </div>
            </div>

            <div className="meta-box">
              <div className="box-label">Pickup Date & Slot</div>
              <div className="box-val">
                {pickupDetails.pickupDate ? pickupDetails.pickupDate.format('DD-MM-YYYY') : '-'}
                {pickupDetails.pickupSlot ? ` (${pickupDetails.pickupSlot})` : ''}
              </div>
            </div>

            {pickupDetails.pickupType === 'another' && (
              <>
                <div className="meta-box">
                  <div className="box-label">AWB Number</div>
                  <div className="box-val">
                    {pickupDetails.awbNumber ? (
                      <Tooltip title="Click to copy AWB">
                        <span
                          style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                          onClick={() => handleCopy(pickupDetails.awbNumber, 'awb', 'AWB Number')}
                        >
                          {pickupDetails.awbNumber}
                          {copiedKey === 'awb' ? <CheckOutlined style={{ color: '#10b981' }} /> : <CopyOutlined />}
                        </span>
                      </Tooltip>
                    ) : (
                      '-'
                    )}
                  </div>
                </div>

                <div className="meta-box">
                  <div className="box-label">Tracking Link</div>
                  <div className="box-val">
                    {pickupDetails.trackingUrl ? (
                      <a href={pickupDetails.trackingUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#0d9488', textDecoration: 'underline' }}>
                        Track Package
                      </a>
                    ) : (
                      '-'
                    )}
                  </div>
                </div>
              </>
            )}

            <div className="meta-box" style={{ gridColumn: pickupDetails.pickupType === 'another' ? undefined : '1 / -1' }}>
              <div className="box-label">Bank UTR Number</div>
              <div className="box-val">
                {pickupDetails.utrNumber ? (
                  <Tooltip title="Click to copy UTR Number">
                    <span
                      style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                      onClick={() => handleCopy(pickupDetails.utrNumber, 'utr', 'UTR Number')}
                    >
                      {pickupDetails.utrNumber}
                      {copiedKey === 'utr' ? <CheckOutlined style={{ color: '#10b981' }} /> : <CopyOutlined />}
                    </span>
                  </Tooltip>
                ) : (
                  '-'
                )}
              </div>
            </div>
          </div>
        </LogisticsCard>
      ) : (
        <Form
          form={pickupForm}
          layout="vertical"
          initialValues={pickupDetails || {}}
          onFinish={handlePickupSubmit}
          disabled={savingPickup}
        >
          <Form.Item label="Pickup Mode">
            <Segmented
              className="pickup-type-segmented"
              block
              options={[
                { label: 'Self Pickup', value: 'self' },
                { label: 'Courier Partner', value: 'another' },
              ]}
              value={pickupMode}
              onChange={setPickupMode}
              style={{ padding: '4px', borderRadius: 10 }}
            />
          </Form.Item>

          <Form.Item
            label="Pickup Person Name"
            name="pickupPersonName"
            rules={[{ required: true, whitespace: true, message: 'Please enter pickup person name' }]}
          >
            <Input placeholder="Enter pickup person name" style={{ borderRadius: 8 }} />
          </Form.Item>

          <Form.Item
            label="Mobile Number"
            name="pickupPersonNumber"
            rules={[
              { required: true, message: 'Please enter pickup person number' },
              { pattern: /^[0-9]{10}$/, message: 'Enter a valid 10 digit number' },
            ]}
          >
            <Input placeholder="Enter 10-digit mobile number" maxLength={10} style={{ borderRadius: 8 }} />
          </Form.Item>

          {pickupMode === 'another' && (
            <>
              <Form.Item
                label="Pickup Partner Name"
                name="pickupPartnerName"
                rules={[{ required: true, whitespace: true, message: 'Please enter pickup partner name' }]}
              >
                <Input placeholder="e.g. Delhivery, Blue Dart" style={{ borderRadius: 8 }} />
              </Form.Item>

              <Form.Item
                label="AWB Number"
                name="awbNumber"
                rules={[{ required: true, whitespace: true, message: 'Please enter AWB number' }]}
              >
                <Input placeholder="Enter AWB number" style={{ borderRadius: 8 }} />
              </Form.Item>

              <Form.Item
                label="Tracking URL"
                name="trackingUrl"
                rules={[
                  { required: true, whitespace: true, message: 'Please enter tracking URL' },
                  { type: 'url', message: 'Enter a valid tracking URL' },
                ]}
              >
                <Input placeholder="https://..." style={{ borderRadius: 8 }} />
              </Form.Item>
            </>
          )}

          <Form.Item
            label="Pickup Date"
            name="pickupDate"
            rules={[{ required: true, message: 'Please select pickup date' }]}
          >
            <DatePicker
              className="pickup-date-picker"
              disabledDate={(current) => current && current.endOf('day').valueOf() < Date.now()}
              format="DD-MM-YYYY"
              style={{ height: '42px', width: '100%', borderRadius: 8 }}
            />
          </Form.Item>

          <Form.Item
            label="Pickup Time Slot"
            name="pickupSlot"
            rules={[{ required: true, message: 'Please select pickup slot' }]}
          >
            <Select
              placeholder="Select pickup slot"
              style={{ height: '42px', width: '100%' }}
              options={[
                { label: '10:00 AM - 1:00 PM', value: '10-1' },
                { label: '1:00 PM - 4:00 PM', value: '1-4' },
                { label: '4:00 PM - 7:00 PM', value: '4-7' },
                { label: '7:00 PM - 10:00 PM', value: '7-10' },
              ]}
            />
          </Form.Item>

          <div style={{ margin: '14px 0 10px', display: 'flex', alignItems: 'center', gap: 6, color: '#0f172a', fontWeight: 700, fontSize: 13 }}>
            <CreditCardOutlined style={{ color: '#0d9488' }} />
            <span>Settlement Payment Details</span>
          </div>

          <Form.Item
            label="UTR / Bank Reference Number"
            name="utrNumber"
            rules={[{ required: true, whitespace: true, message: 'Please enter UTR number' }]}
          >
            <Input placeholder="Enter UTR transaction reference" style={{ borderRadius: 8 }} />
          </Form.Item>

          <Button
            type="primary"
            htmlType="submit"
            block
            loading={savingPickup}
            style={{
              height: 44,
              borderRadius: 10,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
              borderColor: '#0d9488',
              boxShadow: '0 4px 12px rgba(13, 148, 136, 0.25)',
            }}
          >
            Submit Pickup & Payment Details
          </Button>
        </Form>
      )}
    </SideCard>
  );
};

export default PickupPaymentDetailsCard;
